import {
  BULLET_CHARS,
  BULLET_PATTERNS_SLATE,
  ORDERED_LIST_PATTERNS_SLATE,
} from '../bulletPatterns.js';

/**
 * Processes Slate fragments to convert numbered paragraphs to ordered lists
 * Handles Word's numbered list format in Slate fragments
 */
export class SlateListProcessor {
  convertNumberedLists(fragment) {
    if (!Array.isArray(fragment)) {
      return fragment;
    }

    const result = [];
    let currentOrderedItems = [];
    let expectedNumber = 1;

    fragment.forEach((node) => {
      if (node && node.type === 'p' && node.children) {
        const firstChild = node.children[0];
        const isNumberedItem =
          firstChild &&
          firstChild.text &&
          /^\d+[.)]$/.test(firstChild.text.trim());

        if (isNumberedItem) {
          const numberMatch = firstChild.text.match(/^(\d+)/);
          const currentNumber = numberMatch
            ? parseInt(numberMatch[1], 10)
            : null;
          const isSequential = currentNumber === expectedNumber;
          const isStartOfNewList = currentNumber === 1;

          if (
            isSequential ||
            (isStartOfNewList && currentOrderedItems.length === 0)
          ) {
            let cleanedChildren = [];

            for (let i = 1; i < node.children.length; i++) {
              const child = node.children[i];
              if (child.text && child.text.trim() !== '') {
                cleanedChildren.push({ text: child.text.trim() });
              } else if (child.text === undefined) {
                cleanedChildren.push(child);
              }
            }

            if (cleanedChildren.length === 0) {
              cleanedChildren = [{ text: '' }];
            }

            currentOrderedItems.push({
              type: 'li',
              children: cleanedChildren,
            });
            expectedNumber++;
          } else {
            if (currentOrderedItems.length > 0) {
              result.push({
                type: 'ol',
                children: currentOrderedItems,
              });
              currentOrderedItems = [];
            }

            if (currentNumber === 1) {
              let cleanedChildren = [];
              for (let i = 1; i < node.children.length; i++) {
                const child = node.children[i];
                if (child.text && child.text.trim() !== '') {
                  cleanedChildren.push({ text: child.text.trim() });
                } else if (child.text === undefined) {
                  cleanedChildren.push(child);
                }
              }
              if (cleanedChildren.length === 0) {
                cleanedChildren = [{ text: '' }];
              }

              currentOrderedItems.push({
                type: 'li',
                children: cleanedChildren,
              });
              expectedNumber = 2;
            } else {
              result.push(node);
            }
          }
        } else {
          if (currentOrderedItems.length > 0) {
            result.push({
              type: 'ol',
              children: currentOrderedItems,
            });
            currentOrderedItems = [];
            expectedNumber = 1;
          }
          result.push(node);
        }
      } else {
        if (currentOrderedItems.length > 0) {
          result.push({
            type: 'ol',
            children: currentOrderedItems,
          });
          currentOrderedItems = [];
          expectedNumber = 1;
        }
        result.push(node);
      }
    });

    if (currentOrderedItems.length > 0) {
      result.push({
        type: 'ol',
        children: currentOrderedItems,
      });
    }

    return result;
  }
}

/**
 * Cleans bullet characters and empty nodes from Slate fragments
 */
export class SlateNodeCleaner {
  cleanSeparateBulletNodes(node) {
    if (
      !node ||
      (node.type !== 'p' && node.type !== 'li') ||
      !node.children ||
      node.children.length < 2
    ) {
      return node;
    }

    const bulletChars = BULLET_CHARS;
    const children = [...node.children];

    if (
      children.length >= 2 &&
      children[0].text !== undefined &&
      children[1].text !== undefined &&
      bulletChars.includes(children[0].text.trim()) &&
      children[1].text.trim() === ''
    ) {
      const cleanedChildren = children.slice(2);

      if (cleanedChildren.length === 0) {
        return null;
      }

      return {
        ...node,
        children: cleanedChildren,
      };
    }

    return node;
  }

  cleanBulletTextFromListItems(node) {
    if (
      !node ||
      node.type !== 'li' ||
      !node.children ||
      node.children.length === 0
    ) {
      return node;
    }

    const bulletPatterns = BULLET_PATTERNS_SLATE;
    const orderedPatterns = ORDERED_LIST_PATTERNS_SLATE;
    const allPatterns = [...bulletPatterns, ...orderedPatterns];

    const cleanedChildren = node.children.map((child, index) => {
      if (child.text !== undefined) {
        let cleanedText = child.text;

        const isFirstTextNode =
          index === 0 ||
          node.children
            .slice(0, index)
            .every(
              (prevChild) =>
                prevChild.text !== undefined && prevChild.text.trim() === '',
            );

        if (isFirstTextNode) {
          for (const pattern of allPatterns) {
            if (pattern.test(cleanedText)) {
              cleanedText = cleanedText.replace(pattern, '');
              break;
            }
          }
        }

        return {
          ...child,
          text: cleanedText,
        };
      }

      return child;
    });

    return {
      ...node,
      children: cleanedChildren,
    };
  }

  filterEmptyNodes(fragment) {
    if (!Array.isArray(fragment)) {
      return fragment;
    }

    return fragment
      .map((node) => {
        if (node && node.type === 'p') {
          return this.cleanSeparateBulletNodes(node);
        }
        if (node && node.type === 'li') {
          const cleanedSeparateNodes = this.cleanSeparateBulletNodes(node);
          if (cleanedSeparateNodes === null) return null;
          return this.cleanBulletTextFromListItems(cleanedSeparateNodes);
        }
        if (
          node &&
          (node.type === 'ul' || node.type === 'ol') &&
          node.children
        ) {
          return {
            ...node,
            children: node.children
              .map((child) => {
                if (child && child.type === 'li') {
                  const cleanedSeparateNodes =
                    this.cleanSeparateBulletNodes(child);
                  if (cleanedSeparateNodes === null) return null;
                  return this.cleanBulletTextFromListItems(
                    cleanedSeparateNodes,
                  );
                }
                return child;
              })
              .filter((child) => child !== null),
          };
        }
        return node;
      })
      .filter((node) => {
        if (node === null) {
          return false;
        }
        if (typeof node === 'object' && node.text !== undefined) {
          const text = node.text.trim();
          if (text === '' || text.replace(/\u00A0/g, '') === '') {
            return false;
          }
          return true;
        }

        if (node && node.type === 'p') {
          if (!node.children || node.children.length === 0) {
            return false;
          }

          const hasContent = node.children.some((child) => {
            if (child.text !== undefined) {
              const text = child.text.trim();
              return text.length > 0 && text.replace(/\u00A0/g, '') !== '';
            }
            if (child.children && Array.isArray(child.children)) {
              return child.children.some((grandchild) => {
                if (grandchild.text !== undefined) {
                  const text = grandchild.text.trim();
                  return text.length > 0 && text.replace(/\u00A0/g, '') !== '';
                }
                return true;
              });
            }
            return true;
          });

          if (!hasContent) {
            return false;
          }
        }

        return true;
      });
  }
}
